import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import AuthService from "../AuthService";
import { PortalLoading, TeacherPageHeader, TextField, useTeacherPortal } from "./teacherPortalUtils";

function resolveMediaUrl(value) {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  const apiBase = axiosInstance.defaults?.baseURL || "";
  const backendBase = apiBase.replace(/\/api\/?$/, "").replace(/\/$/, "");
  return value.startsWith("/") && backendBase ? `${backendBase}${value}` : value;
}

function TeacherProfilePage() {
  const { data, loading, reload } = useTeacherPortal("teacher-portal/profile/");
  const [form, setForm] = useState({});
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data?.profile) setForm(data.profile);
  }, [data]);

  const previewUrl = useMemo(() => {
    if (photo) return URL.createObjectURL(photo);
    return resolveMediaUrl(form.profile_picture_url);
  }, [photo, form.profile_picture_url]);

  useEffect(() => () => {
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  if (loading && !data) return <PortalLoading label="Loading profile..." />;

  const save = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const payload = new FormData();
      [
        "phone", "alternative_phone", "city", "province", "country", "address",
        "qualification", "highest_degree", "specialist_area", "professional_summary",
      ].forEach((field) => payload.append(field, form[field] || ""));
      if (photo) payload.append("profile_picture", photo);

      const response = await axiosInstance.patch("teacher-portal/profile/", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const updatedProfile = response.data.profile || {};
      const profilePictureUrl = resolveMediaUrl(
        updatedProfile.profile_picture_url
      );

      AuthService.updateCurrentUser({
        full_name: updatedProfile.full_name,
        first_name: updatedProfile.first_name,
        last_name: updatedProfile.last_name,
        email: updatedProfile.email,
        phone: updatedProfile.phone,
        profile_picture: profilePictureUrl,
        profile_picture_url: profilePictureUrl,
      });

      toast.success("Professional profile updated.");
      setForm(updatedProfile);
      setPhoto(null);
      reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <TeacherPageHeader
        title="My Professional Profile"
        subtitle="Maintain your profile picture, qualifications, specialism and contact details. Teaching allocations remain admin controlled."
        icon="bi-person-badge"
        actions={<button className="btn btn-outline-success" onClick={reload}>Refresh</button>}
      />
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <form className="row g-4" onSubmit={save}>
            <div className="col-lg-3">
              <div className="border rounded-4 p-3 text-center h-100 bg-light">
                {previewUrl ? (
                  <img src={previewUrl} alt="Teacher profile" className="rounded-circle object-fit-cover border mb-3" style={{ width: 150, height: 150 }} />
                ) : (
                  <div className="rounded-circle border bg-white d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 150, height: 150 }}>
                    <i className="bi bi-person fs-1 text-muted"></i>
                  </div>
                )}
                <label className="btn btn-outline-success w-100">
                  <i className="bi bi-camera me-2"></i>Change Profile Picture
                  <input type="file" accept="image/*" className="d-none" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
                </label>
                <small className="text-muted d-block mt-2">Use a clear professional head-and-shoulders photograph.</small>
              </div>
            </div>

            <div className="col-lg-9">
              <div className="row g-3">
                <TextField label="Full Name" value={form.full_name} onChange={() => {}} />
                <TextField label="Staff Number" value={form.staff_number} onChange={() => {}} />
                <TextField label="Email" value={form.email} onChange={() => {}} />
                <TextField label="Phone" value={form.phone} onChange={(value) => setForm({ ...form, phone: value })} />
                <TextField label="Alternative Phone" value={form.alternative_phone} onChange={(value) => setForm({ ...form, alternative_phone: value })} />
                <TextField label="Department" value={form.department} onChange={() => {}} />
                <TextField label="Main Qualification" value={form.qualification} onChange={(value) => setForm({ ...form, qualification: value })} />
                <TextField label="Highest Degree" value={form.highest_degree} onChange={(value) => setForm({ ...form, highest_degree: value })} />
                <TextField label="Specialist Area" value={form.specialist_area} onChange={(value) => setForm({ ...form, specialist_area: value })} />
                <TextField label="City" value={form.city} onChange={(value) => setForm({ ...form, city: value })} />
                <TextField label="Province" value={form.province} onChange={(value) => setForm({ ...form, province: value })} />
                <TextField label="Country" value={form.country} onChange={(value) => setForm({ ...form, country: value })} />
                <div className="col-12">
                  <label className="form-label fw-semibold">Professional Summary</label>
                  <textarea className="form-control" rows="3" value={form.professional_summary || ""} onChange={(e) => setForm({ ...form, professional_summary: e.target.value })} placeholder="Briefly describe teaching experience, strengths and specialist responsibilities." />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Residential Address</label>
                  <textarea className="form-control" rows="2" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                <div className="col-12">
                  <button className="btn btn-success px-4" disabled={saving}>{saving ? "Saving..." : "Save Professional Profile"}</button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default TeacherProfilePage;
